/**
 * MyGuruPage — Module 10 personal command center.
 *
 * Every data point is sourced from the canonical /api/account/my-guru/overview
 * aggregate; empty/unavailable metrics render honest empty states rather
 * than fabricated values (Section 11, 14, 15). Photo upload uses the
 * canonical /api/account/me/avatar endpoint.
 */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import {
  Loader2, Camera, Edit3, Phone, Mail, MapPin, Calendar, CheckCircle2,
  ChevronRight, ArrowRight, ClipboardCheck, Star, TrendingUp, Activity,
  Sparkles, Shield, User as UserIcon, Bell, Settings as SettingsIcon,
  LineChart, LogOut, ShieldCheck, Info, Download,
} from 'lucide-react';
import { MyTodoCard } from '../../components/admin/MyTodoCard';
import { useWebPush } from '../../hooks/useWebPush';
import InstallAppDialog from '../../components/admin/InstallAppDialog';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import CalendarSyncDialog from '../../components/admin/CalendarSyncDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = [
  ['monday', 'Monday'], ['tuesday', 'Tuesday'], ['wednesday', 'Wednesday'],
  ['thursday', 'Thursday'], ['friday', 'Friday'],
  ['saturday', 'Saturday'], ['sunday', 'Sunday'],
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const initialsFrom = (name) =>
  (name || 'GG').split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch { return ''; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
};
const fmtTime12 = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
};
const fmtHM = (s) => {
  if (!s) return '';
  const [h, m] = s.split(':').map(Number);
  const p = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${p}`;
};

const roleLabelFor = (u) => {
  const r = (u.role || '').toLowerCase();
  if (r === 'master_admin') return 'Master Admin';
  if (r === 'admin') return 'Admin';
  if (r === 'cleaner') return 'Guru';
  return u.role || 'Team';
};

// Deterministic "Member since" derived from users.created_at
const memberSince = (iso) => (iso ? `Member since ${fmtDate(iso)}` : null);

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
const ProfileHero = ({ profile, onEditPhoto, onEditProfile }) => {
  const roleLabel = roleLabelFor(profile);
  const position = (profile.position || '').trim();
  const activeLabel = profile.is_active === false ? 'Inactive' : 'Active';
  const activeTint = profile.is_active === false ? 'text-zinc-400' : 'text-emerald-400';

  return (
    <div
      data-testid="my-guru-hero"
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-white p-5 md:p-7 shadow-xl ring-1 ring-zinc-800"
    >
      {/* subtle abstract accent */}
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#F56F05]/10 blur-3xl pointer-events-none" aria-hidden />
      <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-[#F56F05]/5 blur-3xl pointer-events-none" aria-hidden />

      <div className="relative flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
        {/* Photo */}
        <div className="flex-shrink-0 mx-auto md:mx-0">
          <div className="relative">
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-full ring-2 ring-zinc-700 overflow-hidden bg-zinc-800">
              {profile.photo_url ? (
                <img src={profile.photo_url} alt={profile.full_name || 'Profile'} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-white bg-gradient-to-br from-[#F56F05] to-orange-700" data-testid="my-guru-initials">
                  {initialsFrom(profile.full_name)}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onEditPhoto}
              data-testid="edit-photo-btn"
              aria-label="Update profile photo"
              className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#F56F05] hover:bg-orange-600 text-white flex items-center justify-center shadow-lg ring-2 ring-zinc-950 transition-colors"
            >
              <Camera className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Identity */}
        <div className="flex-1 min-w-0 text-center md:text-left">
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white" data-testid="hero-name">
              {profile.full_name || 'Set your name'}
            </h2>
            {profile.role === 'master_admin' && (
              <ShieldCheck className="w-5 h-5 text-[#F56F05]" aria-label="Master admin" data-testid="hero-verified" />
            )}
          </div>
          <p className="text-[13px] text-zinc-400 mt-1" data-testid="hero-position-role">
            {[position, roleLabel].filter(Boolean).join(' \u00b7 ')}
          </p>

          {/* Badges: Role + Position (canonical only) */}
          <div className="flex flex-wrap justify-center md:justify-start gap-1.5 mt-3">
            <Badge tint="red">   {roleLabel.toUpperCase()} </Badge>
            {position && <Badge tint="orange">{position.toUpperCase()}</Badge>}
          </div>

          {/* Status + member since */}
          <div className="flex flex-wrap justify-center md:justify-start items-center gap-3 mt-3 text-[12px]">
            <span className="inline-flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${profile.is_active === false ? 'bg-zinc-400' : 'bg-emerald-400'}`} />
              <span className={activeTint}>{activeLabel}</span>
            </span>
            {memberSince(profile.created_at) && (
              <span className="text-zinc-500">{memberSince(profile.created_at)}</span>
            )}
          </div>
        </div>

        {/* Contact block */}
        <div className="md:border-l md:border-zinc-800 md:pl-6 space-y-1.5 text-[13px] text-zinc-300 md:min-w-[220px]">
          <ContactRow icon={Phone}    value={profile.phone} />
          <ContactRow icon={Mail}     value={profile.email} />
          {(profile.city || profile.state) && (
            <ContactRow icon={MapPin} value={[profile.city, profile.state].filter(Boolean).join(', ')} />
          )}
          {profile.created_at && (
            <ContactRow icon={Calendar} value={memberSince(profile.created_at)} />
          )}
        </div>
      </div>

      {profile.bio && (
        <p className="relative mt-5 text-sm text-zinc-300 leading-relaxed max-w-4xl" data-testid="hero-bio">
          {profile.bio}
        </p>
      )}
    </div>
  );
};

const Badge = ({ children, tint }) => {
  const map = {
    red:    'bg-red-500/10 text-red-300 border-red-500/30',
    orange: 'bg-[#F56F05]/15 text-orange-300 border-[#F56F05]/30',
    green:  'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  }[tint] || 'bg-zinc-500/10 text-zinc-300 border-zinc-500/30';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[9px] font-bold tracking-widest rounded border ${map}`}>
      {children}
    </span>
  );
};

const ContactRow = ({ icon: Icon, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
      <span className="truncate">{value}</span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Profile Completeness
// ---------------------------------------------------------------------------
const CompletenessCard = ({ completeness, onImprove }) => {
  const { percent = 0, missing = [] } = completeness || {};
  const circumference = 2 * Math.PI * 44;
  const offset = circumference * (1 - percent / 100);
  const complete = percent >= 100;

  return (
    <Card className="border-zinc-200 shadow-sm" data-testid="completeness-card">
      <CardContent className="p-5 flex flex-col items-center text-center">
        <p className="text-[11px] uppercase tracking-widest font-bold text-zinc-500 mb-3">
          Profile Completeness
        </p>
        <div className="relative w-28 h-28 mb-3">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100" aria-hidden>
            <circle cx="50" cy="50" r="44" stroke="#F1F5F9" strokeWidth="8" fill="none" />
            <circle
              cx="50" cy="50" r="44" stroke="#F56F05" strokeWidth="8" fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{ transition: 'stroke-dashoffset 500ms ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-black text-zinc-900" data-testid="completeness-percent">{percent}%</span>
            <span className="text-[10px] text-zinc-500 tracking-wide">Complete</span>
          </div>
        </div>
        <p className="text-[12px] text-zinc-600 mb-3">
          {complete ? 'You\u2019re all set. Nice work.' : missing.length > 3 ? 'Let\u2019s fill in the essentials.' : 'Nice work! Keep your profile up to date.'}
        </p>
        {missing.length > 0 && (
          <div className="w-full mb-3">
            <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5">Missing</p>
            <div className="flex flex-wrap justify-center gap-1.5" data-testid="completeness-missing">
              {missing.map(m => (
                <span key={m} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-orange-50 text-orange-700 border border-orange-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F56F05]" /> {m}
                </span>
              ))}
            </div>
          </div>
        )}
        <Button
          onClick={onImprove}
          className="w-full bg-[#F56F05] hover:bg-orange-600"
          data-testid="improve-profile-btn"
          disabled={complete}
        >
          {complete ? 'Profile complete' : 'Improve Profile'}
        </Button>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------
const SectionTitle = ({ icon: Icon, children, action }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-widest text-zinc-500 uppercase">
      {Icon && <Icon className="w-3.5 h-3.5 text-[#F56F05]" />}
      {children}
    </div>
    {action}
  </div>
);

// ---------------------------------------------------------------------------
// Today's Focus
// ---------------------------------------------------------------------------
const TodayFocusCard = ({ focus }) => {
  const navigate = useNavigate();
  const next = focus?.next;
  const hasTaskSummary = (focus?.tasks_overdue || 0) + (focus?.tasks_due_today || 0) + (focus?.tasks_open || 0) > 0;

  return (
    <Card className="border-zinc-200 shadow-sm h-full" data-testid="today-focus-card">
      <CardContent className="p-5 flex flex-col h-full">
        <SectionTitle icon={ClipboardCheck}>Today&apos;s Focus</SectionTitle>
        {!next && !hasTaskSummary ? (
          <EmptyBlock icon={ClipboardCheck} title={'You\u2019re clear for today.'} subtitle="Upcoming assignments will appear here." />
        ) : (
          <div className="flex-1 flex flex-col justify-between">
            <div>
              {next && (
                <>
                  <p className="text-[12px] text-zinc-500">{focus.count > 0 ? 'Next Clean' : 'Next Assignment'}</p>
                  <p className="text-3xl font-black text-[#F56F05] mt-0.5" data-testid="today-focus-time">
                    {fmtTime12(next.scheduled_at) || '—'}
                  </p>
                  <p className="text-sm font-bold text-zinc-900 mt-2" data-testid="today-focus-service">
                    {next.service || 'Job'}
                  </p>
                  {next.customer_name && (<p className="text-[12px] text-zinc-600">{next.customer_name}</p>)}
                  {next.location && (<p className="text-[12px] text-zinc-500 mt-0.5">{next.location}</p>)}
                </>
              )}

              {hasTaskSummary && (
                <div className={`${next ? 'mt-4 pt-3 border-t border-zinc-100' : ''}`} data-testid="today-focus-tasks">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5">Tasks</p>
                  <div className="flex flex-wrap gap-1.5">
                    {focus.tasks_due_today > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-orange-50 text-[#F56F05] border border-orange-100" data-testid="task-pill-due-today">
                        {focus.tasks_due_today} Due Today
                      </span>
                    )}
                    {focus.tasks_overdue > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700 border border-red-100" data-testid="task-pill-overdue">
                        {focus.tasks_overdue} Overdue
                      </span>
                    )}
                    {focus.tasks_open > 0 && focus.tasks_open !== focus.tasks_due_today + focus.tasks_overdue && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-zinc-100 text-zinc-700">
                        {focus.tasks_open} Open
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            {next && (
              <Button
                onClick={() => navigate(`/admin/calendar?appt=${encodeURIComponent(next.appointment_id)}`)}
                className="w-full mt-4 bg-[#F56F05] hover:bg-orange-600"
                data-testid="view-job-btn"
              >
                View Job <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Snapshot
// ---------------------------------------------------------------------------
const SnapshotCard = ({ snapshot }) => (
  <Card className="border-zinc-200 shadow-sm h-full" data-testid="snapshot-card">
    <CardContent className="p-5">
      <SectionTitle icon={LineChart}>My Guru Snapshot</SectionTitle>
      <div className="grid grid-cols-2 gap-4">
        <Metric label="Cleans Completed" value={snapshot?.cleans_completed?.value ?? 0} caption="All Time" testId="metric-cleans" />
        <Metric label="Upcoming Jobs"    value={snapshot?.upcoming_jobs?.value ?? 0}   caption="Next up"  testId="metric-upcoming" />
        <Metric
          label="Client Rating"
          value={snapshot?.client_rating?.available ? `${snapshot.client_rating.average}` : '—'}
          suffix={snapshot?.client_rating?.available ? '/ 5' : null}
          caption={snapshot?.client_rating?.available
            ? `Based on ${snapshot.client_rating.count} review${snapshot.client_rating.count === 1 ? '' : 's'}`
            : 'No reviews yet'}
          testId="metric-rating"
          starred={snapshot?.client_rating?.available}
        />
        <Metric
          label="On-Time Rate"
          value="—"
          caption="Available once tracked"
          testId="metric-ontime"
        />
      </div>
    </CardContent>
  </Card>
);

const Metric = ({ label, value, suffix, caption, starred, testId }) => (
  <div data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</p>
    <p className="text-2xl font-black text-zinc-900 mt-1 flex items-baseline gap-1">
      {starred && <Star className="w-4 h-4 text-[#F56F05] fill-[#F56F05]" />}
      <span>{value}</span>
      {suffix && <span className="text-sm text-zinc-400 font-semibold">{suffix}</span>}
    </p>
    {caption && <p className="text-[10px] text-zinc-400 mt-0.5">{caption}</p>}
  </div>
);

// ---------------------------------------------------------------------------
// Availability
// ---------------------------------------------------------------------------
const AvailabilityCard = ({ availability, onEdit }) => {
  const days = availability?.days || {};
  return (
    <Card className="border-zinc-200 shadow-sm h-full" data-testid="availability-card">
      <CardContent className="p-5">
        <SectionTitle icon={Calendar} action={
          <button onClick={onEdit} className="text-[11px] font-bold text-[#F56F05] hover:underline" data-testid="edit-availability-btn">Edit</button>
        }>My Availability</SectionTitle>
        <div className="divide-y divide-zinc-100">
          {DAYS.map(([key, label]) => {
            const d = days[key] || {};
            const enabled = !!d.enabled;
            return (
              <div key={key} className="flex items-center justify-between py-1.5 text-[13px]" data-testid={`availability-row-${key}`}>
                <span className="text-zinc-700 font-medium">{label}</span>
                {enabled && d.start && d.end ? (
                  <span className="inline-flex items-center gap-1.5 text-zinc-800">
                    <span>{fmtHM(d.start)} – {fmtHM(d.end)}</span>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  </span>
                ) : (
                  <span className="text-zinc-400 text-[12px]">Unavailable</span>
                )}
              </div>
            );
          })}
        </div>
        {!availability?.set && (
          <p className="mt-3 text-[11px] text-zinc-500 italic">Set your weekly availability so we can assign you appropriate jobs.</p>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Upcoming Assignments
// ---------------------------------------------------------------------------
const UpcomingCard = ({ items }) => {
  const navigate = useNavigate();
  return (
    <Card className="border-zinc-200 shadow-sm" data-testid="upcoming-card">
      <CardContent className="p-5">
        <SectionTitle icon={Calendar} action={
          <button onClick={() => navigate('/admin/calendar')} className="text-[11px] font-bold text-[#F56F05] hover:underline">View All</button>
        }>Upcoming Assignments</SectionTitle>
        {!items || items.length === 0 ? (
          <EmptyBlock icon={Calendar} title="No upcoming assignments." subtitle="When you're assigned to a job, it will land here first." />
        ) : (
          <div className="divide-y divide-zinc-100">
            {items.map(a => (
              <div key={a.assignment_id} className="flex items-center gap-3 py-3" data-testid={`upcoming-row-${a.assignment_id}`}>
                <div className="w-14 flex-shrink-0 text-center">
                  <p className="text-[10px] uppercase font-bold text-zinc-500 tracking-wide">
                    {new Date(a.scheduled_at).toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
                  </p>
                  <p className="text-[12px] font-bold text-zinc-800">
                    {new Date(a.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-semibold text-zinc-500">{fmtTime12(a.scheduled_at)}</span>
                    <span className="text-sm font-bold text-zinc-900 truncate">{a.service || 'Job'}</span>
                  </div>
                  {a.customer_name && <p className="text-[12px] text-zinc-600 truncate">{a.customer_name}</p>}
                  {a.location && <p className="text-[11px] text-zinc-400 truncate">{a.location}</p>}
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate(`/admin/calendar?appt=${encodeURIComponent(a.appointment_id)}`)}>
                  View Job <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Recent Activity
// ---------------------------------------------------------------------------
const ACTIVITY_ICON = {
  profile_updated:       UserIcon,
  availability_updated:  Calendar,
  assignment_added:      ClipboardCheck,
  assignment_removed:    Info,
  job_completed:         CheckCircle2,
  review_received:       Star,
};

const RecentActivityCard = ({ items }) => (
  <Card className="border-zinc-200 shadow-sm" data-testid="activity-card">
    <CardContent className="p-5">
      <SectionTitle icon={Activity}>Recent Activity</SectionTitle>
      {!items || items.length === 0 ? (
        <EmptyBlock icon={Activity} title="No recent activity yet." subtitle="Your actions and job events will appear here." />
      ) : (
        <div className="space-y-3">
          {items.map((e, i) => {
            const Icon = ACTIVITY_ICON[e.type] || Activity;
            return (
              <div key={i} className="flex gap-3" data-testid={`activity-row-${i}`}>
                <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-zinc-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-zinc-900">{e.label}</p>
                  {e.detail && <p className="text-[12px] text-zinc-500 truncate">{e.detail}</p>}
                </div>
                <span className="text-[10px] text-zinc-400 flex-shrink-0 mt-1">{fmtDateTime(e.at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// Performance
// ---------------------------------------------------------------------------
const PerformanceCard = ({ perf }) => (
  <Card className="border-zinc-200 shadow-sm" data-testid="performance-card">
    <CardContent className="p-5">
      <SectionTitle icon={TrendingUp}>
        Performance <span className="text-zinc-400 font-normal">({perf?.window_days || 30} days)</span>
      </SectionTitle>
      <div className="grid grid-cols-2 gap-4">
        <Metric
          label="Jobs Completed"
          value={perf?.jobs_completed?.value ?? 0}
          caption="Canonical assignments"
          testId="perf-jobs"
        />
        <Metric
          label="Client Rating"
          value={perf?.client_rating?.available ? `${perf.client_rating.average}` : '—'}
          suffix={perf?.client_rating?.available ? '/ 5' : null}
          caption={perf?.client_rating?.available
            ? `${perf.client_rating.count} review${perf.client_rating.count === 1 ? '' : 's'}`
            : 'Available once reviews land'}
          starred={perf?.client_rating?.available}
          testId="perf-rating"
        />
      </div>
      <p className="mt-4 text-[11px] text-zinc-400 italic">
        On-time arrival and quality metrics will appear here once tracking is enabled.
      </p>
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// Profile & Preferences bottom row
// ---------------------------------------------------------------------------
const PrefTile = ({ icon: Icon, title, subtitle, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="flex items-center gap-3 p-4 bg-white rounded-xl border border-zinc-200 hover:border-[#F56F05]/60 hover:shadow-md transition-all text-left w-full"
  >
    <div className="w-9 h-9 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-[#F56F05]" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[13px] font-bold text-zinc-900">{title}</p>
      <p className="text-[11px] text-zinc-500 truncate">{subtitle}</p>
    </div>
    <ChevronRight className="w-4 h-4 text-zinc-400" />
  </button>
);

// ---------------------------------------------------------------------------
// Empty block
// ---------------------------------------------------------------------------
const EmptyBlock = ({ icon: Icon, title, subtitle }) => (
  <div className="py-6 text-center text-zinc-500">
    <Icon className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
    <p className="text-[13px] font-semibold text-zinc-700">{title}</p>
    {subtitle && <p className="text-[11px] text-zinc-500 mt-1">{subtitle}</p>}
  </div>
);

// ===========================================================================
// Edit dialogs
// ===========================================================================
const PersonalInfoDialog = ({ open, onClose, profile, headers, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
        position: profile.position || '',
        bio: profile.bio || '',
      });
    }
  }, [open, profile]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/account/me`, form, { headers });
      toast.success('Profile updated');
      onSaved(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-personal-dialog">
        <DialogHeader>
          <DialogTitle>Personal Information</DialogTitle>
          <DialogDescription>Update the personal details that show on your My Guru profile.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <FieldRow label="Full Name">
            <Input value={form.full_name || ''} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} data-testid="input-full-name" />
          </FieldRow>
          <FieldRow label="Phone">
            <Input value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="input-phone" placeholder="(503) 555-0100" />
          </FieldRow>
          <FieldRow label="Position" hint="Your operational role, e.g. Owner, Lead Guru, Guru. Different from your system permissions.">
            <Input value={form.position || ''} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} data-testid="input-position" placeholder="Owner" />
          </FieldRow>
          <FieldRow label="Professional Bio">
            <Textarea value={form.bio || ''} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} data-testid="input-bio" rows={4} placeholder="A short intro for your team and customers." />
          </FieldRow>
          <p className="text-[11px] text-zinc-500 italic">
            Email and system role are managed by an administrator to keep your account secure.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#F56F05] hover:bg-orange-600" data-testid="save-personal-btn">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AvailabilityDialog = ({ open, onClose, current, headers, onSaved }) => {
  const [days, setDays] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const seed = {};
      DAYS.forEach(([key]) => {
        const d = current?.days?.[key] || {};
        seed[key] = { enabled: !!d.enabled, start: d.start || '08:00', end: d.end || '18:00' };
      });
      setDays(seed);
    }
  }, [open, current]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      DAYS.forEach(([key]) => {
        const d = days[key];
        payload[key] = { enabled: !!d.enabled, start: d.enabled ? d.start : null, end: d.enabled ? d.end : null };
      });
      const r = await axios.put(`${API}/api/account/me/availability`, payload, { headers });
      toast.success('Availability updated');
      onSaved(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save availability');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-availability-dialog">
        <DialogHeader>
          <DialogTitle>My Availability</DialogTitle>
          <DialogDescription>Set the days and hours you&apos;re available for assignments.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2 max-h-[60vh] overflow-y-auto">
          {DAYS.map(([key, label]) => {
            const d = days[key] || {};
            return (
              <div key={key} className="flex items-center gap-3 py-2 border-b border-zinc-100 last:border-0">
                <div className="w-24 text-[13px] font-semibold text-zinc-800">{label}</div>
                <Switch
                  checked={!!d.enabled}
                  onCheckedChange={(v) => setDays(prev => ({ ...prev, [key]: { ...prev[key], enabled: v } }))}
                  data-testid={`avail-toggle-${key}`}
                />
                <div className="flex-1 flex items-center gap-2">
                  {d.enabled ? (
                    <>
                      <Input type="time" value={d.start || ''} className="h-8 flex-1"
                        onChange={e => setDays(prev => ({ ...prev, [key]: { ...prev[key], start: e.target.value } }))} />
                      <span className="text-xs text-zinc-400">–</span>
                      <Input type="time" value={d.end || ''} className="h-8 flex-1"
                        onChange={e => setDays(prev => ({ ...prev, [key]: { ...prev[key], end: e.target.value } }))} />
                    </>
                  ) : (
                    <span className="text-[12px] text-zinc-400 italic">Unavailable</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#F56F05] hover:bg-orange-600" data-testid="save-availability-btn">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PhotoDialog = ({ open, onClose, headers, hasPhoto, onSaved }) => {
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error('Please choose an image under 2 MB.');
    setSaving(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const rdr = new FileReader();
        rdr.onload = () => res(rdr.result); rdr.onerror = rej;
        rdr.readAsDataURL(file);
      });
      const r = await axios.post(`${API}/api/account/me/avatar`, { data_url: dataUrl }, { headers });
      toast.success('Profile photo updated');
      onSaved(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setSaving(false); }
  };
  const remove = async () => {
    setSaving(true);
    try {
      const r = await axios.delete(`${API}/api/account/me/avatar`, { headers });
      toast.success('Photo removed');
      onSaved(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-photo-dialog">
        <DialogHeader>
          <DialogTitle>Profile Photo</DialogTitle>
          <DialogDescription>Your photo appears in the sidebar, My Guru hero, and everywhere your identity shows up.</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-3 text-center">
          <p className="text-sm text-zinc-500">PNG, JPEG, WEBP or GIF up to 2 MB.</p>
          <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" ref={fileRef} onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid="photo-input" />
          <Button className="w-full bg-[#F56F05] hover:bg-orange-600" onClick={() => fileRef.current?.click()} disabled={saving} data-testid="choose-photo-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Camera className="w-4 h-4 mr-1.5" />}
            Choose Image
          </Button>
          {hasPhoto && (
            <Button variant="outline" className="w-full" onClick={remove} disabled={saving} data-testid="remove-photo-btn">
              Remove Photo
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PasswordDialog = ({ open, onClose, headers }) => {  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ current_password: '', new_password: '', confirm: '' }); }, [open]);
  const save = async () => {
    if (form.new_password !== form.confirm) return toast.error("New passwords don't match");
    if (form.new_password.length < 8) return toast.error('New password must be at least 8 characters');
    setSaving(true);
    try {
      await axios.post(`${API}/api/account/me/password`, {
        current_password: form.current_password, new_password: form.new_password,
      }, { headers });
      toast.success('Password updated');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="password-dialog">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>Enter your current password and pick a new one (minimum 8 characters).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <FieldRow label="Current Password">
            <Input type="password" value={form.current_password} onChange={e => setForm(f => ({ ...f, current_password: e.target.value }))} data-testid="input-current-pw" />
          </FieldRow>
          <FieldRow label="New Password" hint="Minimum 8 characters.">
            <Input type="password" value={form.new_password} onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))} data-testid="input-new-pw" />
          </FieldRow>
          <FieldRow label="Confirm New Password">
            <Input type="password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} data-testid="input-confirm-pw" />
          </FieldRow>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#F56F05] hover:bg-orange-600" data-testid="save-password-btn">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FieldRow = ({ label, hint, children }) => (
  <div>
    <Label className="text-[12px] font-semibold text-zinc-700">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <p className="text-[11px] text-zinc-500 mt-1">{hint}</p>}
  </div>
);

// ===========================================================================
// Notification Preferences (Module 11 Phase A §15)
// ===========================================================================
const CATEGORIES = [
  { key: 'leads',         label: 'New Leads',            note: 'Alerts when a new inquiry lands. Admins only.',      roleGate: ['admin', 'master_admin'] },
  { key: 'tasks',         label: 'Tasks',                note: 'Task assigned or overdue.',                            roleGate: null },
  { key: 'jobs',          label: 'Job Changes',          note: 'Assignment or schedule updates.',                      roleGate: null },
  { key: 'communication', label: 'Customer Communication', note: 'Inbound customer messages. Admins only.',           roleGate: ['admin', 'master_admin'] },
];

const NotificationPreferencesDialog = ({ open, onClose, headers, userRole }) => {
  const [prefs, setPrefs] = useState({ leads: true, tasks: true, jobs: true, communication: true });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const token = (headers?.Authorization || '').replace('Bearer ', '');
  const { supported, permission, subscribed, enable, disable, busy } = useWebPush(token);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/api/notifications/preferences`, { headers })
      .then(r => setPrefs(r.data?.preferences || prefs))
      .catch(() => {})
      .finally(() => setLoading(false));
     
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/notifications/preferences`, prefs, { headers });
      toast.success('Notification preferences saved');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="notification-prefs-dialog">
        <DialogHeader>
          <DialogTitle>Notification Preferences</DialogTitle>
          <DialogDescription>
            These filter delivery. They never grant access — role permissions are always enforced separately.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-6 text-center text-zinc-400"><Loader2 className="w-4 h-4 animate-spin inline mr-1.5" />Loading…</div>
        ) : (
          <div className="space-y-3 py-2">
            {/* Push toggle — spec §14 explicit user-gesture only */}
            <div className="p-3 border border-zinc-200 rounded-lg bg-zinc-50/50" data-testid="push-toggle-block">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#F56F05]/10 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-4 h-4 text-[#F56F05]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold text-zinc-900">Push notifications</p>
                  {!supported ? (
                    <p className="text-[11px] text-zinc-500 mt-0.5">Not supported on this browser.</p>
                  ) : permission === 'denied' ? (
                    <p className="text-[11px] text-red-600 mt-0.5">Blocked. Enable in your browser settings, then reload.</p>
                  ) : subscribed ? (
                    <p className="text-[11px] text-emerald-600 mt-0.5">Active on this device.</p>
                  ) : (
                    <p className="text-[11px] text-zinc-500 mt-0.5">Get alerts even when the app is closed.</p>
                  )}
                  {supported && permission !== 'denied' && (
                    <Button
                      size="sm"
                      onClick={async () => {
                        const r = subscribed ? await disable() : await enable();
                        if (!r.ok && r.reason && r.reason !== 'default') {
                          toast.error(`Push ${r.reason.replace('_',' ')}`);
                        } else if (r.ok) {
                          toast.success(subscribed ? 'Push disabled' : 'Push enabled');
                        }
                      }}
                      disabled={busy}
                      className={`mt-2 h-8 px-3 text-xs ${subscribed ? 'bg-zinc-200 hover:bg-zinc-300 text-zinc-800' : 'bg-[#F56F05] hover:bg-orange-600'}`}
                      data-testid="push-toggle-btn"
                    >
                      {busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                      {subscribed ? 'Disable' : 'Enable notifications'}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {CATEGORIES.map(c => {
              const restricted = c.roleGate && !c.roleGate.includes(userRole);
              return (
                <label key={c.key} className={`flex items-start gap-3 p-3 border border-zinc-200 rounded-lg ${restricted ? 'opacity-50' : ''}`} data-testid={`prefs-row-${c.key}`}>
                  <Switch
                    checked={!restricted && !!prefs[c.key]}
                    disabled={restricted}
                    onCheckedChange={(v) => setPrefs(p => ({ ...p, [c.key]: v }))}
                    data-testid={`prefs-toggle-${c.key}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-zinc-900">
                      {c.label}
                      {restricted && <span className="ml-1 text-[10px] text-zinc-400 font-normal">(admin only)</span>}
                    </p>
                    <p className="text-[11px] text-zinc-500">{c.note}</p>
                  </div>
                </label>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-[#F56F05] hover:bg-orange-600" data-testid="save-prefs-btn">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===========================================================================
// Main page
// ===========================================================================
const MyGuruPage = () => {
  const { token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogs, setDialogs] = useState({ personal: false, availability: false, photo: false, password: false, prefs: false, install: false, calendarSync: false });
  const [notifPrefs, setNotifPrefs] = useState(null);
  const { installed: isPwaInstalled } = usePwaInstall();

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/account/my-guru/overview`, { headers });
      setOverview(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load My Guru');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const handleProfileSaved = (freshUser) => {
    // Refresh the aggregate + auth context so sidebar/name/photo update everywhere.
    fetchOverview();
    refreshUser?.();
  };

  if (loading || !overview) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center" data-testid="my-guru-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  const p = overview.profile;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="my-guru-page">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 md:py-8 space-y-6">

        {/* Page header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-zinc-900 tracking-tight" data-testid="page-title">My Guru</h1>
            <p className="text-sm text-zinc-500 mt-1">Your personal command center.</p>
          </div>
          <Button
            onClick={() => setDialogs(d => ({ ...d, personal: true }))}
            className="bg-white hover:bg-zinc-50 text-zinc-900 border border-zinc-200 shadow-sm"
            data-testid="edit-profile-btn"
          >
            <Edit3 className="w-4 h-4 mr-1.5" /> Edit Profile
          </Button>
        </div>

        {/* Hero + Completeness */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <ProfileHero
            profile={p}
            onEditPhoto={() => setDialogs(d => ({ ...d, photo: true }))}
            onEditProfile={() => setDialogs(d => ({ ...d, personal: true }))}
          />
          <CompletenessCard
            completeness={overview.completeness}
            onImprove={() => {
              const missing = overview.completeness?.missing || [];
              if (missing.includes('Availability')) setDialogs(d => ({ ...d, availability: true }));
              else if (missing.includes('Profile Photo')) setDialogs(d => ({ ...d, photo: true }));
              else setDialogs(d => ({ ...d, personal: true }));
            }}
          />
        </div>

        {/* Row 2: Today's Focus · My To-Do · Snapshot · Availability */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <TodayFocusCard focus={overview.today_focus} />
          <MyTodoCard
            tasks={overview.my_todo?.tasks || []}
            openCount={overview.my_todo?.open_count || 0}
            onChanged={() => fetchOverview()}
          />
          <SnapshotCard   snapshot={overview.snapshot} />
          <AvailabilityCard
            availability={overview.availability}
            onEdit={() => setDialogs(d => ({ ...d, availability: true }))}
          />
        </div>

        {/* Row 3: Upcoming · Recent Activity · Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <UpcomingCard       items={overview.upcoming_assignments} />
          <RecentActivityCard items={overview.recent_activity} />
          <PerformanceCard    perf={overview.performance} />
        </div>

        {/* Profile & Preferences bottom row */}
        <div>
          <SectionTitle icon={Sparkles}>Profile &amp; Preferences</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <PrefTile icon={UserIcon}     title="Personal Information" subtitle="Name, contact, bio, position"
              testId="pref-personal"     onClick={() => setDialogs(d => ({ ...d, personal: true }))} />
            <PrefTile icon={Camera}       title="Profile Photo"        subtitle="Update your photo"
              testId="pref-photo"        onClick={() => setDialogs(d => ({ ...d, photo: true }))} />
            <PrefTile icon={Calendar}     title="Availability"         subtitle="Manage your availability"
              testId="pref-availability" onClick={() => setDialogs(d => ({ ...d, availability: true }))} />
            <PrefTile icon={SettingsIcon} title="Preferences"          subtitle="Notification &amp; system preferences"
              testId="pref-prefs"        onClick={() => setDialogs(d => ({ ...d, prefs: true }))} />
            <PrefTile icon={Shield}       title="Security &amp; Access"    subtitle="Password, account security"
              testId="pref-security"     onClick={() => setDialogs(d => ({ ...d, password: true }))} />
            <PrefTile
              icon={isPwaInstalled ? CheckCircle2 : Download}
              title="Install Gleam Guru App"
              subtitle={isPwaInstalled ? 'Installed on this device' : 'Add to your Home Screen for fast access'}
              testId="pref-install"
              onClick={() => setDialogs(d => ({ ...d, install: true }))}
            />
            <PrefTile
              icon={Calendar}
              title="Calendar Sync"
              subtitle="Sync your work to Apple or Google Calendar"
              testId="pref-calendar-sync"
              onClick={() => setDialogs(d => ({ ...d, calendarSync: true }))}
            />
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <PersonalInfoDialog  open={dialogs.personal}     onClose={() => setDialogs(d => ({ ...d, personal: false }))}     profile={p}            headers={headers} onSaved={handleProfileSaved} />
      <AvailabilityDialog  open={dialogs.availability} onClose={() => setDialogs(d => ({ ...d, availability: false }))} current={overview.availability} headers={headers} onSaved={handleProfileSaved} />
      <PhotoDialog         open={dialogs.photo}        onClose={() => setDialogs(d => ({ ...d, photo: false }))}        hasPhoto={!!p.photo_url} headers={headers} onSaved={handleProfileSaved} />
      <PasswordDialog      open={dialogs.password}     onClose={() => setDialogs(d => ({ ...d, password: false }))}     headers={headers} />
      <NotificationPreferencesDialog
        open={dialogs.prefs}
        onClose={() => setDialogs(d => ({ ...d, prefs: false }))}
        headers={headers}
        userRole={p.role}
      />
      <InstallAppDialog
        open={dialogs.install}
        onClose={() => setDialogs(d => ({ ...d, install: false }))}
      />
      <CalendarSyncDialog
        open={dialogs.calendarSync}
        onClose={() => setDialogs(d => ({ ...d, calendarSync: false }))}
        token={token}
      />
    </div>
  );
};

export default MyGuruPage;
